import { ReactElement, useState } from 'react'

import { Alert, Button, Checkbox, Divider, Typography } from 'antd'
import { Field, FieldProps, Formik, FormikValues } from 'formik'
import map from 'lodash/map'

import { Flex, Modal } from 'components/core'
import { CreateOrganizationModal } from 'components/CreateResourceModal'
import { PlusIcon } from 'components/Icons'
import Box from 'components/Layout/Box'
import OrganizationGroupSearchSelect from 'components/SearchSelect/OrganizationGroupSearchSelect/OrganizationGroupSearchSelect'
import {
    buildSerializableOrgGroupLabel,
    SerializableOrgGroup,
} from 'components/SearchSelect/OrganizationGroupSearchSelect/serializableOrgGroup'
import SerializableOrgGroupDisplay from 'components/SearchSelect/OrganizationGroupSearchSelect/SerializableOrgGroupDisplay'
import OrganizationSearchSelect from 'components/SearchSelect/OrganizationSearchSelect/OrganizationSearchSelect'
import { useModal } from 'hooks'
import useLocalStorage from 'hooks/useLocalStorage'
import {
    AutomationCapabilitiesMap,
    Organization,
    OrganizationGroup,
} from 'types'

import { ImpersonationButtonToggle } from './ImpersonationButtonToggle'
import { PinToggleButton } from './PinToggleButton'

const { Title } = Typography

interface Props {
    modalVisible: boolean
    closeModal: () => void
    currentOrganization: Organization
    currentOrganizationGroup: OrganizationGroup
    handleChangeOrganizationGroup: (
        organizationId: string,
        organizationGroupId: string
    ) => void
    automationCapabilities: AutomationCapabilitiesMap
    hiddenAutomationCapabilities: string[]
    handleChangeHiddenAutomationCapabilities: (
        hiddenAutomations: string[]
    ) => void
}

interface ImpersonationModalFormValues extends FormikValues {
    orgId: string
    orgGroupId: string | undefined
    hiddenAutomations: string[]
}

const ImpersonationModal = ({
    modalVisible,
    closeModal,
    currentOrganization,
    currentOrganizationGroup,
    handleChangeOrganizationGroup,
    automationCapabilities,
    hiddenAutomationCapabilities,
    handleChangeHiddenAutomationCapabilities,
}: Props): ReactElement => {
    const [selectedOrgGroup, setSelectedOrgGroup] = useState<
        SerializableOrgGroup | undefined
    >({
        orgId: currentOrganization.id,
        orgGroupId: currentOrganizationGroup.id,
        label: buildSerializableOrgGroupLabel(
            currentOrganization.name,
            currentOrganizationGroup.name,
            currentOrganizationGroup.agency?.name
        ),
    })

    const {
        isModalVisible: isCreateOrgModalVisible,
        toggleModalVisible: toggleCreateOrgModal,
    } = useModal()

    const [pinnedOrganizationGroups, setPinnedOrganizationGroups] =
        useLocalStorage<SerializableOrgGroup[]>(
            'PinnedImpersonationOrganizations',
            []
        )

    const handlePinOrganizationGroup = (newPin: SerializableOrgGroup): void => {
        setPinnedOrganizationGroups((prev) =>
            [...prev, newPin].toSorted((a, b) => {
                if (a.orgId < b.orgId) {
                    return -1
                }
                if (a.orgId > b.orgId) {
                    return 1
                }
                return 0
            })
        )
    }

    const handleUnpinOrganizationGroup = (orgGroupId: string): void => {
        setPinnedOrganizationGroups((prev) =>
            prev.filter((pinned) => pinned.orgGroupId !== orgGroupId)
        )
    }

    return (
        <>
            <Formik<ImpersonationModalFormValues>
                initialValues={{
                    orgId: currentOrganization.id,
                    orgGroupId: currentOrganizationGroup.id,
                    hiddenAutomations: hiddenAutomationCapabilities,
                }}
                onSubmit={(
                    { orgId, orgGroupId, hiddenAutomations },
                    { setSubmitting }
                ) => {
                    if (orgId && orgGroupId) {
                        if (
                            orgId === currentOrganization.id &&
                            orgGroupId === currentOrganizationGroup.id
                        ) {
                            setSubmitting(false)
                        } else {
                            handleChangeOrganizationGroup(orgId, orgGroupId)
                            handleChangeHiddenAutomationCapabilities(
                                hiddenAutomations
                            )
                        }
                        closeModal()
                    }
                }}
            >
                {({ setFieldValue, handleSubmit, isSubmitting }) => (
                    <Modal
                        title="Impersonation Mode Settings"
                        open={modalVisible}
                        onCancel={closeModal}
                        footer={
                            <Flex justifyContent="space-between">
                                <Button
                                    type="link"
                                    icon={<PlusIcon />}
                                    onClick={() => {
                                        closeModal()
                                        toggleCreateOrgModal()
                                    }}
                                    style={{ paddingLeft: 0 }}
                                >
                                    Create New Organization
                                </Button>
                                <div>
                                    <Button onClick={closeModal}>Cancel</Button>
                                    <Button
                                        style={{ marginLeft: 8 }}
                                        type="primary"
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            handleSubmit()
                                        }}
                                    >
                                        Impersonate
                                    </Button>
                                </div>
                            </Flex>
                        }
                    >
                        <Flex direction="column" alignItems="start">
                            <Alert
                                message={
                                    <>
                                        While operating in Impersonation Mode,
                                        you are accessing real customer data in
                                        production. Changes made to{' '}
                                        <b>anything</b> in Impersonation Mode
                                        will be <b>live in production</b>. Use
                                        extreme caution when making changes.{' '}
                                        <ImpersonationButtonToggle />
                                    </>
                                }
                                type="error"
                                style={{ marginBottom: 12 }}
                            />
                            <Title
                                level={4}
                                style={{ marginBottom: 4, marginTop: 16 }}
                            >
                                Organization Group to Assume
                            </Title>
                            <Field name="orgId">
                                {({
                                    field,
                                    form: {
                                        values: { orgId },
                                    },
                                }: FieldProps) => (
                                    <OrganizationSearchSelect
                                        impersonateAccess
                                        onChange={(value) => {
                                            setFieldValue(field.name, value)
                                            setFieldValue(
                                                'orgGroupId',
                                                undefined
                                            )
                                        }}
                                        selectedOrgId={orgId}
                                        style={{ marginTop: 8 }}
                                    />
                                )}
                            </Field>
                            <Field name="orgGroupId">
                                {({
                                    field,
                                    form: {
                                        values: { orgId, orgGroupId },
                                    },
                                }: FieldProps) => {
                                    const isPinned =
                                        orgGroupId &&
                                        pinnedOrganizationGroups.some(
                                            (o) => o.orgGroupId === orgGroupId
                                        )
                                    return (
                                        <Flex
                                            gap={8}
                                            marginTop={8}
                                            style={{ width: '100%' }}
                                        >
                                            <Box flex style={{ flex: 1 }}>
                                                <OrganizationGroupSearchSelect
                                                    orgId={orgId}
                                                    onChange={(
                                                        value,
                                                        orgGroup
                                                    ) => {
                                                        setFieldValue(
                                                            field.name,
                                                            value
                                                        )
                                                        setSelectedOrgGroup(
                                                            orgGroup
                                                        )
                                                    }}
                                                    selectedOrgGroupId={
                                                        orgGroupId
                                                    }
                                                />
                                            </Box>
                                            <Box style={{ width: 28 }}>
                                                <PinToggleButton
                                                    isPinned={isPinned}
                                                    disabled={!selectedOrgGroup}
                                                    onClick={() => {
                                                        if (isPinned) {
                                                            handleUnpinOrganizationGroup(
                                                                orgGroupId
                                                            )
                                                        } else {
                                                            handlePinOrganizationGroup(
                                                                selectedOrgGroup!
                                                            )
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Flex>
                                    )
                                }}
                            </Field>
                            {pinnedOrganizationGroups.length > 0 && (
                                <Title
                                    level={5}
                                    style={{ marginTop: 20, marginBottom: 4 }}
                                >
                                    Your Pinned Organization Groups
                                </Title>
                            )}
                            {pinnedOrganizationGroups.map((serializable) => (
                                <Flex
                                    key={serializable.orgGroupId}
                                    style={{ width: '100%' }}
                                >
                                    <Button
                                        type="text"
                                        onClick={() => {
                                            setFieldValue(
                                                'orgId',
                                                serializable.orgId
                                            )
                                            setFieldValue(
                                                'orgGroupId',
                                                serializable.orgGroupId
                                            )
                                            setSelectedOrgGroup(serializable)
                                        }}
                                        style={{ width: '100%' }}
                                    >
                                        <SerializableOrgGroupDisplay
                                            serializable={serializable}
                                        />
                                    </Button>
                                    <PinToggleButton
                                        isPinned={true}
                                        onClick={() =>
                                            handleUnpinOrganizationGroup(
                                                serializable.orgGroupId
                                            )
                                        }
                                    />
                                </Flex>
                            ))}
                            <Divider style={{ marginTop: 12 }} />
                            <h3>Hidden Automation Capabilities</h3>
                            <Field name="hiddenAutomations">
                                {({
                                    field,
                                    form: {
                                        values: { hiddenAutomations },
                                    },
                                }: FieldProps) => (
                                    <Checkbox.Group
                                        options={map(
                                            automationCapabilities,
                                            (capability, capabilityId) => ({
                                                label: capability.name,
                                                value: capabilityId,
                                            })
                                        )}
                                        defaultValue={hiddenAutomations}
                                        onChange={(selectedOptions) => {
                                            setFieldValue(
                                                field.name,
                                                selectedOptions
                                            )
                                        }}
                                    />
                                )}
                            </Field>
                        </Flex>
                    </Modal>
                )}
            </Formik>
            <CreateOrganizationModal
                visible={isCreateOrgModalVisible}
                closeModal={toggleCreateOrgModal}
                isImpersonationMode
            />
        </>
    )
}

export default ImpersonationModal
